import type { Limit } from '../types';
import * as lendingApi from '../api/lending';
import { KeyedAsyncCache } from './asyncCache';

export interface RawLimit {
  totalLimitVnd: number;
  usedAmountVnd: number;
  lockedAmountVnd: number;
  lockExpiresAt?: string;
  pendingLoanId?: string;
  tier: lendingApi.BackendRiskTier;
}

/** The real backend keeps exactly ONE running limit per farmer (no per-contract/
 * per-year split like the mock's `Limit[]`) — `LimitService` never models more than
 * that. `getLimitsByFarmer` below wraps it in a single-item array so existing list-
 * shaped call sites keep working. */
function toFrontendLimit(l: lendingApi.BackendLimit): Limit {
  return {
    id: l._id,
    farmerId: l.farmerId,
    loaiDinhMuc: 'Vay vốn sản xuất',
    hanMuc: Number(l.totalLimitVnd),
    // Combined on purpose — see the Limit type's own doc comment.
    daSuDung: Number(l.usedAmountVnd) + Number(l.lockedAmountVnd),
    daSuDungThucTe: Number(l.usedAmountVnd),
    dangKhoa: Number(l.lockedAmountVnd),
    nam: new Date().getFullYear(),
  };
}

function toRawLimit(l: lendingApi.BackendLimit): RawLimit {
  return {
    totalLimitVnd: Number(l.totalLimitVnd),
    usedAmountVnd: Number(l.usedAmountVnd),
    lockedAmountVnd: Number(l.lockedAmountVnd),
    lockExpiresAt: l.lockExpiresAt,
    pendingLoanId: l.pendingLoanId,
    tier: l.tier,
  };
}

const byFarmer = new KeyedAsyncCache<lendingApi.BackendLimit | null>(
  (farmerId) => lendingApi.getLimitByFarmer(farmerId),
  null,
  'limits:byFarmer',
);

export function getLimitsByFarmer(farmerId?: string): Limit[] {
  if (!farmerId) return [];
  const limit = byFarmer.get(farmerId);
  return limit ? [toFrontendLimit(limit)] : [];
}

export function getRawLimitByFarmer(farmerId?: string): RawLimit | null {
  if (!farmerId) return null;
  const limit = byFarmer.get(farmerId);
  return limit ? toRawLimit(limit) : null;
}

export function refreshLimit(farmerId: string): void {
  byFarmer.invalidate(farmerId);
  byFarmer.get(farmerId);
}
